import type { components } from "../lib/api/schema";
import { ApiErrorCause } from "./parseApiError";

/**
 * Whether a failed request is worth retrying automatically.
 *
 * Only 503: the backend sends it as retryable backpressure (pool saturation,
 * with a Retry-After). The exception is a DATA_UNAVAILABLE 503, which is not
 * transient. Everything else stays one-shot — a 4xx won't change on retry,
 * and a 500 is not known to be transient.
 */
export function isRetryableApiError(error: Error): boolean {
  const cause = error.cause as ApiErrorCause | undefined;
  // 9 mirrors appretio.error.ErrorCode.DATA_UNAVAILABLE: reference data missing
  // on the server (e.g. a tenant calendar short of the planning horizon). A data
  // fault, not backpressure, so a retry gets the same answer.
  const dataUnavailable: components["schemas"]["ErrorCode"] = 9;
  return cause?.status === 503 && cause.code !== dataUnavailable;
}

/**
 * Delay before retry attempt `failureCount` (1-based): the server's
 * Retry-After when it sent one, else exponential backoff capped at 30 s.
 */
export function getRetryDelayMs(failureCount: number, error: Error): number {
  const cause = error.cause as ApiErrorCause | undefined;
  if (cause?.retryAfterSeconds) {
    return cause.retryAfterSeconds * 1000;
  }
  return Math.min(1000 * 2 ** failureCount, 30000);
}
